import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import steamSearchService from "../../service/steamSearch.service";
import CardsCheckout from "../nintendoDeals/CardsCheckout";
import RegionSelect from "../nintendoDeals/RegionSelect";
import SteamTopUpWidget from "../steam/SteamTopUpWidget";
import {
  STEAM_STORE_ID,
  TOPUP_REGIONS,
  USD_CARDS_REGION,
  USD_CODES_REGIONS,
  buyOptions,
  formatRub,
  rubForPrice,
} from "./steamTopup";

const SEARCH_PLACEHOLDER = "Найти игру: «ведьмак», «elden ring», «киберпанк»…";

const panelClass =
  "rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#2a2a2a]";

function money(value, currency) {
  if (value == null) return "";
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: currency || "USD",
      currencyDisplay: currency === "KZT" ? "narrowSymbol" : "symbol",
      maximumFractionDigits: currency === "USD" ? 2 : 0,
    }).format(value);
  } catch {
    return `${value} ${currency || ""}`.trim();
  }
}

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function PriceLine({ game }) {
  if (game.price == null) {
    return <span className="text-gray-400">нет цены в этом регионе</span>;
  }
  return (
    <>
      <span className="font-semibold text-gray-900 dark:text-white">
        {game.price === 0 ? "бесплатно" : money(game.price, game.currency)}
      </span>
      {game.original ? (
        <span className="ml-1.5 text-xs text-gray-400 line-through">
          {money(game.original, game.currency)}
        </span>
      ) : null}
      {game.percent ? (
        <span className="ml-1.5 rounded bg-green-100 px-1 text-xs font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300">
          −{game.percent}%
        </span>
      ) : null}
    </>
  );
}

const planText = (plan) =>
  `${plan.items.map((item) => `${item.quantity} × $${item.face}`).join(" + ")}${
    plan.items.length > 1 || plan.items[0]?.quantity > 1 ? ` = $${plan.face}` : ""
  }`;

const BADGE_TONES = {
  green: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  blue: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
};

function OptionCard({ title, price, note, selected, badge, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex flex-col rounded-xl border p-3 text-left transition ${
        selected
          ? "border-indigo-500 bg-indigo-50/60 ring-2 ring-indigo-100 dark:bg-indigo-900/20 dark:ring-indigo-900/50"
          : "border-gray-200 hover:border-indigo-300 dark:border-gray-600"
      }`}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-white">{title}</span>
        {badge ? (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${BADGE_TONES[badge.tone]}`}
          >
            {badge.text}
          </span>
        ) : null}
      </span>
      <span className="mt-1 text-lg font-bold text-indigo-700 dark:text-indigo-300">{price}</span>
      <span className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">{note}</span>
    </button>
  );
}

function PickedGame({
  game,
  region,
  label,
  pricing,
  usdCards,
  cardsSettled,
  onRefreshCards,
  initialOpen,
  onTryKz,
}) {
  const options = buyOptions(game, region, pricing, usdCards);
  const { codes, topup } = options;
  const both = Boolean(codes && topup);
  const [selected, setSelected] = useState(null);
  const autoSelect = useRef(initialOpen);
  const ready = pricing.settled && (cardsSettled || !USD_CODES_REGIONS.has(region));
  useEffect(() => {
    if (autoSelect.current && options.best && ready) {
      autoSelect.current = false;
      setSelected(options.best);
    }
  }, [options.best, ready]);
  const toggle = (value) => {
    autoSelect.current = false;
    setSelected((current) => (current === value ? null : value));
  };
  const rub = rubForPrice(game.price, game.currency, pricing.rates);
  const exact = game.currency === "RUB";

  return (
    <div className={`${panelClass} mt-3 p-3 sm:p-4`}>
      <div className="flex gap-3 sm:gap-4">
        <img
          src={game.cover}
          alt=""
          onError={(event) => {
            if (game.image && event.currentTarget.src !== game.image) {
              event.currentTarget.src = game.image;
            }
          }}
          className="aspect-[460/215] w-32 shrink-0 self-start rounded-lg bg-gray-100 object-cover sm:w-48"
        />
        <div className="min-w-0 flex-1">
          <p className="font-semibold leading-snug text-gray-900 dark:text-white">{game.title}</p>
          <p className="mt-1 text-sm">
            <PriceLine game={game} />
          </p>
          {TOPUP_REGIONS.has(region) && rub && !exact ? (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              ≈ {formatRub(rub)} пополнения по курсу сервиса
            </p>
          ) : null}
          <a
            href={game.url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Открыть в Steam
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {game.price == null && onTryKz ? (
        <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          <p>
            В российском Steam у этой игры нет цены — чаще всего издатель закрыл продажи в России
            (так с GTA, «Ведьмаком», Assassin's Creed). Обычно её можно купить на аккаунт
            Казахстана: кодами США или пополнением по логину.
          </p>
          <button
            type="button"
            onClick={onTryKz}
            className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500"
          >
            Посмотреть цену в Казахстане
          </button>
        </div>
      ) : game.price == null ? (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          В регионе {label} у игры нет цены: она бесплатная или издатель её здесь не продаёт.
        </p>
      ) : codes || topup ? (
        <>
          <p className="mb-2 mt-3 text-sm font-medium text-gray-700 dark:text-gray-200">
            {both ? "Как купить — выберите удобный способ:" : "Как купить:"}
          </p>
          <div className={`grid gap-2 ${both ? "sm:grid-cols-2" : ""}`}>
            {codes ? (
              <OptionCard
                title={`Коды Steam США · ${codes.plan.codes} ${
                  codes.plan.codes > 1 ? "шт." : "код"
                }`}
                price={formatRub(codes.cost)}
                note={`${planText(codes.plan)}${
                  region === "kz"
                    ? ` — хватит на ≈ $${codes.need.toFixed(2)}. При активации Steam сам переведёт доллары в тенге, остаток останется на балансе.`
                    : codes.plan.overpay > 0
                      ? `, остаток $${codes.plan.overpay.toFixed(2)} останется на балансе`
                      : ""
                }`}
                selected={selected === "codes"}
                badge={
                  both
                    ? options.best === "codes"
                      ? { text: "выгоднее", tone: "green" }
                      : { text: "надёжнее", tone: "blue" }
                    : null
                }
                onClick={() => toggle("codes")}
              />
            ) : null}
            {topup ? (
              <OptionCard
                title="Пополнить по логину"
                price={topup.cost ? `${exact ? "" : "≈ "}${formatRub(topup.cost)}` : "…"}
                note={`${exact ? "" : "≈ "}${formatRub(topup.amount)} на баланс Steam${
                  topup.belowMin ? " (минимум пополнения)" : ""
                }${
                  topup.capped
                    ? ` — это максимум за одно пополнение, на игру за ≈ ${formatRub(rub)} не хватит`
                    : ""
                } + комиссия сервиса. Без кодов: деньги придут прямо на аккаунт.`}
                selected={selected === "topup"}
                badge={both && options.best === "topup" ? { text: "выгоднее", tone: "green" } : null}
                onClick={() => toggle("topup")}
              />
            ) : null}
          </div>
          {selected === "codes" && codes ? (
            <p className="mt-2 flex gap-2 rounded-lg bg-sky-50 px-3 py-2 text-xs leading-snug text-sky-900 dark:bg-sky-900/30 dark:text-sky-100">
              <ShieldCheckIcon className="h-4 w-4 shrink-0 text-sky-600 dark:text-sky-300" />
              <span>
                Хотя бы раз пополните аккаунт кодом. Если потеряете доступ к почте или забудете
                пароль, поддержка Steam восстановит аккаунт по коду и дате его активации.
              </span>
            </p>
          ) : null}

          {selected === "codes" && codes ? (
            <>
              <CardsCheckout
                plan={codes.plan}
                storeId={STEAM_STORE_ID}
                onPriceChanged={onRefreshCards}
              />
              <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                Коды придут на email и на страницу заказа. Активация: Steam → «Игры» →
                «Активировать код Steam Wallet».
              </p>
            </>
          ) : null}
          {selected === "topup" && topup ? (
            <div className="mt-3">
              <SteamTopUpWidget
                initialRub={exact ? game.price : 0}
                initialKzt={game.currency === "KZT" ? game.price : 0}
              />
              <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                Сумма подставлена под цену игры{exact ? "" : " по примерному курсу"} — её можно
                изменить. Минимум пополнения — 150 ₽.
              </p>
            </div>
          ) : null}
        </>
      ) : USD_CODES_REGIONS.has(region) ? (
        <Link
          to={`/store/steam?region=${USD_CARDS_REGION}`}
          className="mt-3 block w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-indigo-500"
        >
          Купить карты Steam США
        </Link>
      ) : null}
    </div>
  );
}

/** Поиск игры в Steam с ценой региона и пополнением под неё прямо на странице. */
function SteamGameSearch({
  region,
  regions = [],
  onRegionChange,
  pricing,
  usdCards,
  cardsSettled,
  onRefreshCards,
  externalPick,
}) {
  const [text, setText] = useState("");
  const [search, setSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const boxRef = useRef(null);
  const query = useDebounced(text.trim(), 300);
  const label = regions.find((item) => item.value === region)?.label;
  const hasKz = regions.some((item) => item.value === "kz");

  useEffect(() => {
    setPicked(null);
  }, [region]);

  useEffect(() => {
    if (!externalPick) return;
    setOpen(false);
    setPicked(externalPick);
    boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [externalPick]);

  useEffect(() => {
    if (query.length < 2) {
      setSearch(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    steamSearchService
      .search(region, query, controller.signal)
      .then((data) => {
        setSearch({ ...data, region });
        setOpen(true);
      })
      .catch((error) => {
        if (error.name !== "AbortError") setSearch({ error: true, region });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [query, region]);

  useEffect(() => {
    const close = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <section ref={boxRef} className="mx-auto mt-5 max-w-3xl px-1">
      <div className="relative">
        <div className="flex items-stretch rounded-2xl border border-gray-200 bg-white shadow-sm transition focus-within:border-indigo-400 focus-within:ring-4 focus-within:ring-indigo-100 dark:border-gray-600 dark:bg-[#2a2a2a] dark:focus-within:ring-indigo-900/50">
          {regions.length > 1 ? (
            <RegionSelect value={region} regions={regions} onChange={onRegionChange} />
          ) : null}
          <MagnifyingGlassIcon className="ml-3 h-5 w-5 shrink-0 self-center text-gray-400" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
            autoComplete="off"
            aria-label="Поиск игры в Steam"
            placeholder={SEARCH_PLACEHOLDER}
            className="min-w-0 flex-1 bg-transparent px-3 py-3.5 text-base text-gray-900 outline-none placeholder:text-gray-400 dark:text-white"
          />
          {loading ? (
            <span className="mr-4 self-center text-xs text-gray-400">ищем…</span>
          ) : null}
        </div>

        {open && search?.region === region ? (
          <div className={`${panelClass} absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden`}>
            {search.error ? (
              <p className="px-3 py-3 text-sm text-gray-500">
                Поиск временно недоступен, попробуйте позже.
              </p>
            ) : search.results?.length ? (
              search.results.map((game) => (
                <button
                  key={game.appId}
                  type="button"
                  onClick={() => {
                    setPicked({ game, openTopup: false });
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-indigo-50 dark:hover:bg-gray-700"
                >
                  <img
                    src={game.image || game.cover}
                    alt=""
                    loading="lazy"
                    className="h-9 w-24 shrink-0 rounded bg-gray-100 object-cover"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-gray-900 dark:text-white">
                      {game.title}
                    </span>
                    <span className="block text-sm">
                      <PriceLine game={game} />
                    </span>
                  </span>
                </button>
              ))
            ) : (
              <p className="px-3 py-3 text-sm text-gray-500">
                Ничего не нашлось. Попробуйте другое написание или английское название.
              </p>
            )}
          </div>
        ) : null}
      </div>

      <p className="mt-2 hidden px-1 text-xs text-gray-400 sm:block">
        {region === "kz"
          ? "Выберите игру — посчитаем, сколько нужно кодов США или на сколько пополнить по логину"
          : TOPUP_REGIONS.has(region)
            ? "Выберите игру — подставим сумму пополнения Steam под её цену"
            : "Выберите игру — посчитаем, сколько нужно кодов Steam США"}
      </p>

      {picked ? (
        <PickedGame
          key={`${picked.game.appId}-${region}`}
          game={picked.game}
          region={region}
          label={label}
          pricing={pricing}
          usdCards={usdCards}
          cardsSettled={cardsSettled}
          onRefreshCards={onRefreshCards}
          initialOpen={Boolean(picked.openTopup)}
          onTryKz={
            region === "ru" && hasKz
              ? () => {
                  setText(picked.game.title);
                  onRegionChange("kz");
                  boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              : null
          }
        />
      ) : null}
    </section>
  );
}

export default SteamGameSearch;
